import { format, subDays } from 'date-fns';
import { toast } from 'sonner';

const claimDate = (c) => c.timestamp?.split('T')[0] || c.timestamp?.split(' ')[0];

export const processReportData = (claims, filters, families) => {
  let filteredClaims = [...claims];

  if (filters.dateFrom) {
    filteredClaims = filteredClaims.filter(c => claimDate(c) >= filters.dateFrom);
  }
  if (filters.dateTo) {
    filteredClaims = filteredClaims.filter(c => claimDate(c) <= filters.dateTo);
  }
  if (filters.hospital && filters.hospital !== 'all') {
    filteredClaims = filteredClaims.filter(c => c.hospital_name === filters.hospital);
  }
  if (filters.status && filters.status !== 'all') {
    filteredClaims = filteredClaims.filter(c => c.status?.toUpperCase() === filters.status.toUpperCase());
  }
  if (filters.member) {
    const searchTerm = filters.member.toLowerCase();
    filteredClaims = filteredClaims.filter(c =>
      c.patient_name?.toLowerCase().includes(searchTerm) ||
      c.patient_serial_number?.toLowerCase().includes(searchTerm)
    );
  }
  if (filters.policy) {
    filteredClaims = filteredClaims.filter(c =>
      c.family_id?.toLowerCase().includes(filters.policy.toLowerCase())
    );
  }

  // Summary stats
  const totalClaims = filteredClaims.length;
  const totalAmount = filteredClaims.reduce((sum, c) => sum + (c.total_claim_amount || 0), 0);
  const pendingClaims = filteredClaims.filter(c => c.status?.toUpperCase() === 'PENDING');
  const approvedClaims = filteredClaims.filter(c => c.status?.toUpperCase() === 'PAID' || c.status?.toUpperCase() === 'APPROVED');
  const rejectedClaims = filteredClaims.filter(c => c.status?.toUpperCase() === 'REJECTED');
  const avgClaimAmount = totalClaims > 0 ? totalAmount / totalClaims : 0;

  const statusDistribution = [
    { name: 'Pending', value: pendingClaims.length, amount: pendingClaims.reduce((s, c) => s + (c.total_claim_amount || 0), 0) },
    { name: 'Approved/Paid', value: approvedClaims.length, amount: approvedClaims.reduce((s, c) => s + (c.total_claim_amount || 0), 0) },
    { name: 'Rejected', value: rejectedClaims.length, amount: rejectedClaims.reduce((s, c) => s + (c.total_claim_amount || 0), 0) }
  ].filter(s => s.value > 0);

  // Daily claims trend
  const dailyTrend = {};
  filteredClaims.forEach(c => {
    const date = claimDate(c);
    if (date) {
      if (!dailyTrend[date]) dailyTrend[date] = { date, count: 0, amount: 0 };
      dailyTrend[date].count++;
      dailyTrend[date].amount += c.total_claim_amount || 0;
    }
  });
  const dailyTrendData = Object.values(dailyTrend).sort((a, b) => a.date.localeCompare(b.date));

  // Member Utilization
  const memberStats = {};
  filteredClaims.forEach(c => {
    const key = c.patient_serial_number;
    if (!memberStats[key]) {
      memberStats[key] = {
        serial_number: c.patient_serial_number,
        patient_name: c.patient_name,
        family_id: c.family_id,
        claim_count: 0,
        total_amount: 0,
        claims: []
      };
    }
    memberStats[key].claim_count++;
    memberStats[key].total_amount += c.total_claim_amount || 0;
    memberStats[key].claims.push(c);
  });
  const memberUtilization = Object.values(memberStats)
    .sort((a, b) => b.total_amount - a.total_amount)
    .slice(0, 50);

  // Hospital Analysis
  const hospitalStats = {};
  filteredClaims.forEach(c => {
    const key = c.hospital_name;
    if (!hospitalStats[key]) {
      hospitalStats[key] = {
        hospital_name: key,
        claim_count: 0,
        total_amount: 0,
        pending_amount: 0,
        paid_amount: 0,
        rejected_count: 0
      };
    }
    hospitalStats[key].claim_count++;
    hospitalStats[key].total_amount += c.total_claim_amount || 0;
    if (c.status?.toUpperCase() === 'PENDING') {
      hospitalStats[key].pending_amount += c.total_claim_amount || 0;
    } else if (c.status?.toUpperCase() === 'PAID' || c.status?.toUpperCase() === 'APPROVED') {
      hospitalStats[key].paid_amount += c.total_claim_amount || 0;
    } else if (c.status?.toUpperCase() === 'REJECTED') {
      hospitalStats[key].rejected_count++;
    }
  });
  const hospitalAnalysis = Object.values(hospitalStats).sort((a, b) => b.total_amount - a.total_amount);

  // Policy Balance Report
  const policyBalance = families.map(f => {
    const familyClaims = filteredClaims.filter(c => c.family_id === f.family_id);
    const totalUsed = familyClaims.reduce((sum, c) => sum + (c.total_claim_amount || 0), 0);
    return {
      family_id: f.family_id,
      principle_member: f.principle_member_name,
      total_allotment: f.total_allotment || 0,
      remaining_balance: f.remaining_balance || 0,
      total_used: totalUsed,
      utilization_pct: f.total_allotment > 0 ? ((f.total_allotment - f.remaining_balance) / f.total_allotment * 100) : 0,
      claim_count: familyClaims.length,
      status: f.status
    };
  }).sort((a, b) => b.utilization_pct - a.utilization_pct);

  // High Cost Claims (top 20 by amount)
  const highCostClaims = [...filteredClaims]
    .sort((a, b) => (b.total_claim_amount || 0) - (a.total_claim_amount || 0))
    .slice(0, 20)
    .map(c => ({
      claim_id: c.claim_id,
      patient_name: c.patient_name,
      hospital_name: c.hospital_name,
      amount: c.total_claim_amount || 0,
      status: c.status,
      date: claimDate(c)
    }));

  // Fraud Detection
  const fraudAlerts = [];

  // High family frequency (>5 claims in 14 days)
  const familyClaimsIn14Days = {};
  const fourteenDaysAgo = format(subDays(new Date(), 14), 'yyyy-MM-dd');
  filteredClaims.forEach(c => {
    if (claimDate(c) >= fourteenDaysAgo) {
      const key = c.family_id;
      if (!familyClaimsIn14Days[key]) familyClaimsIn14Days[key] = [];
      familyClaimsIn14Days[key].push(c);
    }
  });
  Object.entries(familyClaimsIn14Days).forEach(([familyId, familyClaims]) => {
    if (familyClaims.length > 5) {
      fraudAlerts.push({
        type: 'HIGH_FREQUENCY',
        severity: 'HIGH',
        family_id: familyId,
        description: `Family ${familyId} has ${familyClaims.length} claims in the last 14 days`,
        claim_count: familyClaims.length,
        total_amount: familyClaims.reduce((s, c) => s + (c.total_claim_amount || 0), 0),
        details: familyClaims.slice(0, 5)
      });
    }
  });

  // Duplicate claims (same patient, hospital, amount, day)
  const potentialDuplicates = {};
  filteredClaims.forEach(c => {
    const key = `${c.patient_serial_number}_${c.hospital_name}_${c.total_claim_amount}_${c.timestamp?.split('T')[0]}`;
    if (!potentialDuplicates[key]) potentialDuplicates[key] = [];
    potentialDuplicates[key].push(c);
  });
  Object.values(potentialDuplicates).forEach(dupes => {
    if (dupes.length > 1) {
      fraudAlerts.push({
        type: 'DUPLICATE',
        severity: 'MEDIUM',
        family_id: dupes[0].family_id,
        description: `Potential duplicate claims detected for ${dupes[0].patient_name}`,
        claim_count: dupes.length,
        total_amount: dupes.reduce((s, c) => s + (c.total_claim_amount || 0), 0),
        details: dupes
      });
    }
  });

  // Unusual claim amounts (>3x average)
  if (avgClaimAmount > 0) {
    filteredClaims.forEach(c => {
      if ((c.total_claim_amount || 0) > avgClaimAmount * 3) {
        fraudAlerts.push({
          type: 'UNUSUAL_AMOUNT',
          severity: 'LOW',
          family_id: c.family_id,
          description: `Claim ${c.claim_id} amount ($${c.total_claim_amount?.toFixed(2)}) is significantly higher than average ($${avgClaimAmount.toFixed(2)})`,
          claim_count: 1,
          total_amount: c.total_claim_amount || 0,
          details: [c]
        });
      }
    });
  }

  // Monthly Exposure (all claims, not filtered)
  const monthlyData = {};
  claims.forEach(c => {
    const date = claimDate(c);
    if (date) {
      const month = date.substring(0, 7);
      if (!monthlyData[month]) monthlyData[month] = { month, claims: 0, amount: 0, paid: 0, pending: 0 };
      monthlyData[month].claims++;
      monthlyData[month].amount += c.total_claim_amount || 0;
      if (c.status?.toUpperCase() === 'PAID' || c.status?.toUpperCase() === 'APPROVED') {
        monthlyData[month].paid += c.total_claim_amount || 0;
      } else if (c.status?.toUpperCase() === 'PENDING') {
        monthlyData[month].pending += c.total_claim_amount || 0;
      }
    }
  });
  const monthlyExposure = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month)).slice(-12);

  const severityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };

  return {
    summary: {
      totalClaims,
      totalAmount,
      avgClaimAmount,
      pendingCount: pendingClaims.length,
      pendingAmount: pendingClaims.reduce((s, c) => s + (c.total_claim_amount || 0), 0),
      approvedCount: approvedClaims.length,
      approvedAmount: approvedClaims.reduce((s, c) => s + (c.total_claim_amount || 0), 0),
      rejectedCount: rejectedClaims.length,
      statusDistribution,
      dailyTrend: dailyTrendData,
      filteredClaims
    },
    memberUtilization,
    hospitalAnalysis,
    policyBalance,
    highCostClaims,
    fraudAlerts: fraudAlerts.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]),
    monthlyExposure
  };
};

export const exportReportCSV = (activeTab, reportData) => {
  let data = [];
  let filename = 'report';

  switch (activeTab) {
    case 'summary':
      data = reportData.summary.filteredClaims || [];
      filename = 'claims_summary';
      break;
    case 'memberUtilization':
      data = reportData.memberUtilization;
      filename = 'member_utilization';
      break;
    case 'hospitalAnalysis':
      data = reportData.hospitalAnalysis;
      filename = 'hospital_analysis';
      break;
    case 'policyBalance':
      data = reportData.policyBalance;
      filename = 'policy_balance';
      break;
    case 'highCost':
      data = reportData.highCostClaims;
      filename = 'high_cost_claims';
      break;
    case 'fraud':
      data = reportData.fraudAlerts;
      filename = 'fraud_alerts';
      break;
    case 'monthly':
      data = reportData.monthlyExposure;
      filename = 'monthly_exposure';
      break;
    default:
      data = [];
  }

  if (data.length === 0) {
    toast.warning('No data to export');
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(h => {
      const val = row[h];
      if (typeof val === 'object') return JSON.stringify(val);
      if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val ?? '';
    }).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('CSV exported successfully');
};
